import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SigninRequest } from '@interfaces/auth.interface';
import { UserPatchRequest } from '@interfaces/users.interface';

import { useLogoutMutation, useSigninMutation } from '@api/auth.api';
import api from '@api/index';
import { usePatchCurrentUserMutation } from '@api/users.api';

import {
  logout as logoutAction,
  selectCurrentUser,
  setCredentials,
} from '@slices/auth.slice';

const useAuth = () => {
  const dispatch = useDispatch();

  const user = useSelector(selectCurrentUser);

  const [signinMutation, { isLoading: isLoadingSignin }] = useSigninMutation();
  const [logoutMutation, { isLoading: isLoadingLogout }] = useLogoutMutation();
  const [patchMutation, { isLoading: isLoadingPatch }] =
    usePatchCurrentUserMutation();

  const signin = useCallback(
    async (data: SigninRequest) => {
      const user = await signinMutation(data).unwrap();

      dispatch(setCredentials(user));
    },
    [dispatch, signinMutation],
  );

  const logout = useCallback(async () => {
    await logoutMutation().unwrap();

    dispatch(api.util.resetApiState());
    dispatch(logoutAction());
  }, [dispatch, logoutMutation]);

  const patch = useCallback(
    async (body: UserPatchRequest) => {
      const patchedUser = await patchMutation(body).unwrap();
      const updatedUser = {
        ...user,
        ...patchedUser,
      };
      dispatch(setCredentials(updatedUser));
    },
    [dispatch, patchMutation, user],
  );

  return useMemo(
    () => ({
      user,
      isAuthenticated: user !== null,
      signin,
      patch,
      logout,
      isLoading: isLoadingSignin || isLoadingLogout || isLoadingPatch,
    }),
    [
      user,
      signin,
      patch,
      logout,
      isLoadingSignin,
      isLoadingLogout,
      isLoadingPatch,
    ],
  );
};

export default useAuth;
